import {
  Button,
  Icon,
  InlineGrid,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import { ReplaceIcon } from "@shopify/polaris-icons";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const sourceOptions = [
  { label: "Product title", value: "title" },
  { label: "Description", value: "description" },
  { label: "Brand", value: "brand" },
  { label: "Image URL", value: "imageLink" },
  { label: "Google category", value: "googleProductCategory" },
];

const targetOptions = [
  { label: "Title", value: "title" },
  { label: "Description", value: "description" },
  { label: "Brand", value: "brand" },
  { label: "Image URL", value: "imageLink" },
  { label: "Google category", value: "googleProductCategory" },
];

const transformOptions = [
  { label: "Keep value", value: "none" },
  { label: "Uppercase", value: "uppercase" },
  { label: "Lowercase", value: "lowercase" },
];

const FeedRules = () => {
  const { search } = useLocation();
  const shop = new URLSearchParams(search).get("shop");
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_API_URL}/api/shopify/store/settings?shop=${shop}`,
    )
      .then((response) => response.json())
      .then((result) => setRules(result.data?.feedRules || []))
      .catch(() => setMessage("Unable to load feed rules."));
  }, [shop]);

  const updateRule = (index, key, value) => {
    setRules((current) =>
      current.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, [key]: value } : rule,
      ),
    );
  };

  const saveRules = async () => {
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shopify/store/update_settings?shop=${shop}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ settings: { feedRules: rules } }),
        },
      );
      if (!response.ok) throw new Error("Save failed");
      setMessage("Feed rules saved.");
    } catch (error) {
      setMessage("Unable to save feed rules.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-card">
      <div className="settings-card__header">
        <span className="app-icon-tile app-icon-tile--warn">
          <Icon source={ReplaceIcon} tone="base" />
        </span>
        <div>
          <h3>Feed Rules</h3>
          <p>Map product fields before they are submitted to Merchant API.</p>
        </div>
      </div>

      {rules.length === 0 ? (
        <Text as="p" tone="subdued">
          No rules yet. Add a mapping to transform values before upload.
        </Text>
      ) : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rules.map((rule, index) => (
          <InlineGrid key={`${rule.source}-${index}`} columns={4} gap="300">
            <Select
              label="Source"
              labelHidden
              options={sourceOptions}
              value={rule.source || "title"}
              onChange={(value) => updateRule(index, "source", value)}
            />
            <Select
              label="Target"
              labelHidden
              options={targetOptions}
              value={rule.target || "title"}
              onChange={(value) => updateRule(index, "target", value)}
            />
            <Select
              label="Transform"
              labelHidden
              options={transformOptions}
              value={rule.transform || "none"}
              onChange={(value) => updateRule(index, "transform", value)}
            />
            <TextField
              label="Value"
              labelHidden
              value={rule.value || ""}
              onChange={(value) => updateRule(index, "value", value)}
              autoComplete="off"
            />
          </InlineGrid>
        ))}
      </div>

      <div className="settings-actions">
        <Button
          onClick={() =>
            setRules((current) => [
              ...current,
              {
                source: "title",
                target: "title",
                transform: "none",
                value: "",
              },
            ])
          }
        >
          Add rule
        </Button>
        <Button primary loading={saving} onClick={saveRules}>
          Save rules
        </Button>
      </div>

      {message ? (
        <Text tone="subdued" as="p">
          {message}
        </Text>
      ) : null}
    </div>
  );
};

export default FeedRules;
